import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import {
  getValidationError,
  ValidationRule,
} from '../../utility/validation/validation';
import _, { trim } from 'lodash';
import { HttpError } from '../../config/Axios/axios-instance';
import { Asset } from '../../domain/Asset';
import { useIntl } from 'react-intl';
import { RadioProps } from '../../common/TextField/TextField';

const IGNORED_TRIM = ['password', 'file', 'autocomplete', 'custom', 'upload'];

export type LabelPlacement = 'end' | 'start' | 'top' | 'bottom';

export type FormInputBlueprint = {
  name: string;
  label?: ReactNode;
  type: string;
  validation?: Array<ValidationRule>;
  value?: string | string[] | number | File | File[] | null | any[];
  isHidden?: boolean;
  options?: Array<any>;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  capitalize?: boolean;
  locale?: string;
  disableClearable?: boolean;
  multiple?: boolean;
  labelPlacement?: LabelPlacement;
  alternativeNames?: string[];
  asset?: Asset | null;
  creatable?: boolean;
  maxFileCount?: number;
  radioProps?: RadioProps;
};

export type FormInput = FormInputBlueprint & {
  isValidatable?: boolean;
  validationErrors?: Array<string>;
};

export type FormBehavior = {
  submitOnChange?: boolean;
};

export type FormSubmitInput = {
  [key: string]: string;
};

export const useForm = <T>(
  inputBlueprints: Array<FormInputBlueprint>,
  onFormSubmit?: (inputs: T) => void,
  formBehavior?: FormBehavior,
  isUpdateNeeded?: boolean,
) => {
  const intl = useIntl();

  const [inputs, setInputs] = useState<Array<FormInput>>(inputBlueprints);

  useEffect(() => {
    if (isUpdateNeeded) {
      setInputs(inputBlueprints);
    }
  }, [isUpdateNeeded]);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) => {
        return prevInput.name === event.target.name
          ? {
              ...prevInput,
              value: prevInput.capitalize
                ? event.target.value.toUpperCase()
                : event.target.value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(
                    event.target.value,
                    prevInput.validation,
                    intl,
                  )
                : [],
            }
          : { ...prevInput };
      });

      onChangeSubmit(inputs);

      return inputs;
    });
  };

  const onChangeSubmit = (inputs: FormInput[]) => {
    const hasErrors = inputs
      .map((input) => ({
        ...input,
        validationErrors: getValidationError(
          // @ts-ignore
          input.value,
          input.validation,
          intl,
        ),
      }))
      .filter((input) => input.validationErrors?.length);

    if (hasErrors.length) {
      return;
    }

    formBehavior?.submitOnChange &&
      onFormSubmit &&
      onFormSubmit(getSubmitInputs(inputs));
  };

  const onCheckboxChange = (name: string, value: string) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(value, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      ),
    );
  };

  const onInputValueChange = (name: string, value: string) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(value, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      ),
    );
  };

  const onTimeChange = (name: string, value: string) => {
    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(value, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      );

      onChangeSubmit(inputs);

      return inputs;
    });
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const files = event.target.files ? Array.from(event.target.files) : [];

    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === event.target.name
          ? {
              ...prevInput,
              value:
                prevInput.multiple && Array.isArray(prevInput.value)
                  ? [...prevInput.value, ...files]
                  : files[0],
              isValidatable: true,
              validationErrors: files.length
                ? getValidationError(
                    prevInput.multiple ? files : files[0],
                    prevInput.validation,
                    intl,
                  )
                : [],
            }
          : { ...prevInput },
      ),
    );
  };

  const onSelectChange = (value: string | string[] | any[], name: string) => {
    setInputs((prevState) => {
      const inputs = prevState.map((prevInput) => {
        return prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(value, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput };
      });

      onChangeSubmit(inputs);

      return inputs;
    });
  };

  const onClearInput = (name: string) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: '',
              validationErrors: prevInput.isValidatable
                ? getValidationError('', prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      ),
    );
  };

  const onLoseInputFocus = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === event.target.name
          ? {
              ...prevInput,
              isValidatable: true,
              validationErrors: getValidationError(
                prevInput.value instanceof File
                  ? ''
                  : (IGNORED_TRIM.includes(prevInput.type)
                      ? prevInput.value
                      : trim(prevInput.value?.toString())) || '',
                prevInput.validation,
                intl,
              ),
            }
          : { ...prevInput },
      ),
    );
  };

  const onInputBlur = (name: string) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              isValidatable: true,
              validationErrors: getValidationError(
                (IGNORED_TRIM.includes(prevInput.type)
                  ? prevInput.value
                  : trim(prevInput.value?.toString())) || '',
                prevInput.validation,
                intl,
              ),
            }
          : { ...prevInput },
      ),
    );
  };

  const setNewInputObject = (name: string, newValue: any) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              ...newValue,
            }
          : { ...prevInput },
      ),
    );
  };

  const onSubmit = (event?: ChangeEvent) => {
    event?.preventDefault();

    setInputs(
      inputs.map((input) => ({
        ...input,
        validationErrors: [],
      })),
    );

    const validatedInputs = inputs.map((input) => ({
      ...input,
      validationErrors: getValidationError(
        input.value instanceof File
          ? input.value
          : (IGNORED_TRIM.includes(input.type)
              ? input.value
              : trim(input.value?.toString())) || '',
        input.validation,
        intl,
      ),
    }));

    if (
      _.find(
        validatedInputs,
        (validatedInput) => !_.isEmpty(validatedInput.validationErrors),
      )
    ) {
      setInputs(validatedInputs);
      return;
    }

    handleSubmit();
  };

  const handleSubmit = () => {
    onFormSubmit &&
      onFormSubmit(
        Object.assign(
          {},
          ...inputs.map((input) => ({
            [input.name]: IGNORED_TRIM.includes(input.type)
              ? input.value
              : trim(input.value?.toString()),
          })),
        ),
      );
  };

  const onFileDelete = (name: string, file: string | File) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value:
                prevInput.multiple && Array.isArray(prevInput.value)
                  ? prevInput.value.filter((val: string | File) => {
                      if (file instanceof File && val instanceof File) {
                        return file.name !== val.name;
                      }

                      return val !== file;
                    })
                  : '',
              validationErrors: prevInput.isValidatable
                ? getValidationError('', prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      ),
    );
  };

  const getSubmitInputs = (submitInputs: Array<FormInput>) =>
    Object.assign(
      {},
      ...submitInputs.map((input) => ({
        [input.name]:
          IGNORED_TRIM.includes(input.type) || Array.isArray(input.value)
            ? input.value
            : trim(input.value?.toString()),
      })),
    );

  const onSetValidationErrors = (error: HttpError) => {
    if (_.isArray(error)) {
      error.forEach((fieldError) => {
        setInputs((prevState) =>
          prevState.map((prevInput) =>
            prevInput.name === fieldError.field ||
            prevInput.alternativeNames?.includes(fieldError.field)
              ? {
                  ...prevInput,
                  isValidatable: true,
                  validationErrors: [fieldError.message],
                }
              : { ...prevInput },
          ),
        );
      });
    }
  };

  const onRadioChange = (name: string, value: string) => {
    setInputs((prevState) =>
      prevState.map((prevInput) =>
        prevInput.name === name
          ? {
              ...prevInput,
              value: value,
              validationErrors: prevInput.isValidatable
                ? getValidationError(value, prevInput.validation, intl)
                : [],
            }
          : { ...prevInput },
      ),
    );

    formBehavior?.submitOnChange &&
      onFormSubmit &&
      setTimeout(() => {
        onFormSubmit(getSubmitInputs(inputs));
      }, 10);
  };

  return {
    inputs,
    onInputChange,
    onCheckboxChange,
    onTimeChange,
    onFileChange,
    onLoseInputFocus,
    onSubmit,
    onClearInput,
    setNewInputObject,
    onSelectChange,
    onFileDelete,
    getSubmitInputs,
    onSetValidationErrors,
    onInputBlur,
    onInputValueChange,
    onRadioChange,
  };
};
